import React, { useState } from 'react';

const EMPTY_FORM = {
  name: '',
  site_id: '',
  document_type: '',
  source_type: '',
  department: '',
  s3_prefix: '',
  exclude_prefix: '',
  start_delay_seconds: 0,
  enabled: true,
  is_upload_target: false,
};

/**
 * Create/edit form for one sync source (an S3 folder the poller watches).
 *
 * `options` carries the sites and enum values from the API so this form never
 * hardcodes them — a new site or document type shows up here automatically.
 * On edit, `key` is shown read-only: it is the Redis poll-lock identity, so the
 * backend refuses to change it.
 */
const SyncSourceFormModal = ({ source, options, onSubmit, onCancel, saving }) => {
  const isEdit = Boolean(source);
  const [form, setForm] = useState(() =>
    source
      ? {
          name: source.name ?? '',
          site_id: source.site_id ?? '',
          document_type: source.document_type ?? '',
          source_type: source.source_type ?? '',
          department: source.department ?? '',
          s3_prefix: source.s3_prefix ?? '',
          exclude_prefix: source.exclude_prefix ?? '',
          start_delay_seconds: source.start_delay_seconds ?? 0,
          enabled: source.enabled ?? true,
          is_upload_target: source.is_upload_target ?? false,
        }
      : EMPTY_FORM
  );
  const [error, setError] = useState(null);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    if (!form.name.trim()) return setError('Name is required');
    if (!form.site_id) return setError('Site is required');
    if (!form.document_type) return setError('Document type is required');
    if (!form.source_type) return setError('Source type is required');
    if (!form.s3_prefix.trim()) return setError('S3 prefix is required');

    // Empty optional strings must go as null, not '' — the API treats '' as a
    // real value and would store a blank exclude prefix.
    onSubmit({
      ...form,
      name: form.name.trim(),
      s3_prefix: form.s3_prefix.trim(),
      exclude_prefix: form.exclude_prefix.trim() || null,
      department: form.department || null,
      start_delay_seconds: Number(form.start_delay_seconds) || 0,
      is_upload_target: Boolean(form.is_upload_target),
    });
  };

  const inputClass =
    'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-1">
          {isEdit ? 'Edit Sync Source' : 'Add Sync Source'}
        </h3>
        <p className="text-sm text-slate-500 mb-5">
          Changes take effect on the next poll — no restart needed.
        </p>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg text-sm bg-red-50 text-red-700">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="e.g. HO — HR ADM Receipt"
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Site</label>
              <select
                value={form.site_id}
                onChange={(e) => setField('site_id', e.target.value)}
                className={inputClass}
              >
                <option value="">Select site…</option>
                {options.sites.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Document Type</label>
              <select
                value={form.document_type}
                onChange={(e) => setField('document_type', e.target.value)}
                className={inputClass}
              >
                <option value="">Select type…</option>
                {options.document_types.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Source Type</label>
              <select
                value={form.source_type}
                onChange={(e) => setField('source_type', e.target.value)}
                className={inputClass}
              >
                <option value="">Select source…</option>
                {options.source_types.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Department <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <select
                value={form.department}
                onChange={(e) => setField('department', e.target.value)}
                className={inputClass}
              >
                <option value="">None</option>
                {options.departments.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">S3 Prefix</label>
              <input
                type="text"
                value={form.s3_prefix}
                onChange={(e) => setField('s3_prefix', e.target.value)}
                placeholder="e.g. hhome/HR/ADM-Receipt/"
                className={`${inputClass} font-mono`}
              />
              <p className="text-xs text-slate-400 mt-1">
                A trailing slash is added automatically — without it the prefix would also
                match sibling folders with the same leading name.
              </p>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Exclude Prefix <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={form.exclude_prefix}
                onChange={(e) => setField('exclude_prefix', e.target.value)}
                placeholder="e.g. hhome/Purchasing/COMMON/PO/"
                className={`${inputClass} font-mono`}
              />
              <p className="text-xs text-slate-400 mt-1">
                Use when this folder contains a sub-folder already synced by another source.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start Delay (seconds)</label>
              <input
                type="number"
                min={0}
                max={3600}
                value={form.start_delay_seconds}
                onChange={(e) => setField('start_delay_seconds', e.target.value)}
                className={inputClass}
              />
              <p className="text-xs text-slate-400 mt-1">
                Delays this source in a full run, to let an overlapping source finish first.
              </p>
            </div>

            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setField('enabled', e.target.checked)}
                  className="w-4 h-4 accent-blue-600"
                />
                Enabled
              </label>
            </div>

            <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_upload_target}
                  onChange={(e) => setField('is_upload_target', e.target.checked)}
                  className="w-4 h-4 mt-0.5 accent-blue-600"
                />
                <span>
                  Manual upload target
                  <span className="block text-xs text-slate-500 mt-0.5">
                    Files uploaded through the UI for this site and document type land in this
                    folder. Only one source per site + document type can hold this — turning it
                    on here moves it off whichever source has it now.
                  </span>
                </span>
              </label>
            </div>
          </div>

          {isEdit && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Key</label>
              <input
                type="text"
                value={source.key}
                readOnly
                className={`${inputClass} font-mono bg-slate-50 text-slate-500`}
              />
              <p className="text-xs text-slate-400 mt-1">
                Immutable — it identifies the poll lock and any in-flight task.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Source'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SyncSourceFormModal;
